import { FileText, FileSpreadsheet, Download, Trash2 } from 'lucide-react';
import { Button } from './button';

export type FileAttachmentType = 'pdf' | 'doc' | 'docx' | 'xls';

export interface FileAttachmentRowProps {
  /** Unique key for the row (used as data-testid suffix) */
  fileId: string | number;
  filename: string;
  fileType: FileAttachmentType;
  /** Raw byte count - displayed as KB/MB */
  sizeBytes: number;
  /** ISO date string, displayed as localised date */
  uploadedAt: string;
  onDownload?: () => void;
  onDelete?: () => void;
  /** Disable interaction buttons (e.g. while uploading) */
  disabled?: boolean;
}

const FILE_ICONS: Record<FileAttachmentType, React.ReactNode> = {
  pdf: <FileText className="h-4 w-4 text-red-500 shrink-0" />,
  doc: <FileText className="h-4 w-4 text-blue-500 shrink-0" />,
  docx: <FileText className="h-4 w-4 text-blue-500 shrink-0" />,
  xls: <FileSpreadsheet className="h-4 w-4 text-emerald-600 shrink-0" />,
};

const FILE_TYPE_LABELS: Record<FileAttachmentType, string> = {
  pdf: 'PDF',
  doc: 'DOC',
  docx: 'DOCX',
  xls: 'XLS',
};

function formatFileSize(bytes: number): string {
  if (bytes >= 1_000_000) return `${(bytes / 1_000_000).toFixed(1)} MB`;
  return `${Math.round(bytes / 1_000)} KB`;
}

function formatUploadDate(iso: string): string {
  return new Date(iso).toLocaleDateString('pl-PL', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

export function FileAttachmentRow({
  fileId,
  filename,
  fileType,
  sizeBytes,
  uploadedAt,
  onDownload,
  onDelete,
  disabled = false,
}: FileAttachmentRowProps) {
  return (
    <div
      className="flex items-center gap-3 py-2.5 px-3 rounded-lg border border-border/50 bg-muted/20 hover:bg-muted/40 transition-colors"
      data-testid={`file-attachment-row-${fileId}`}
    >
      {/* File type icon */}
      {FILE_ICONS[fileType]}

      {/* File info */}
      <div className="flex-1 min-w-0">
        <p
          className="text-[13px] font-medium text-foreground truncate"
          data-testid={`file-name-${fileId}`}
          title={filename}
        >
          {filename}
        </p>
        <p className="text-[11px] text-muted-foreground mt-0.5">
          <span className="font-mono uppercase">{FILE_TYPE_LABELS[fileType]}</span>
          <span className="mx-1.5 opacity-40">·</span>
          <span>{formatFileSize(sizeBytes)}</span>
          <span className="mx-1.5 opacity-40">·</span>
          <span>{formatUploadDate(uploadedAt)}</span>
        </p>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-1 shrink-0">
        {onDownload ? (
          <Button
            variant="ghost"
            size="icon-sm"
            onClick={onDownload}
            disabled={disabled}
            aria-label={`Pobierz ${filename}`}
            data-testid={`file-download-${fileId}`}
          >
            <Download className="h-3.5 w-3.5" />
          </Button>
        ) : null}
        {onDelete ? (
          <Button
            variant="ghost"
            size="icon-sm"
            onClick={onDelete}
            disabled={disabled}
            aria-label={`Usun ${filename}`}
            data-testid={`file-delete-${fileId}`}
          >
            <Trash2 className="h-3.5 w-3.5 text-muted-foreground" />
          </Button>
        ) : null}
      </div>
    </div>
  );
}
